import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, Camera, Route, Timer, HandHelping, Fuel, PackageSearch, AlertOctagon, CalendarCheck, KeyRound, Compass, Play, Undo2, FileText, CircleCheckBig } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGuia } from "@/components/guia/Guia";
import { t } from "@/lib/i18n"; // Prompt M

// Fix Q (2026-10-13): ayuda del chofer en inglés y con el recorrido nuevo (Start Trip, etapas, Files (N), Finish Trip).
const SECCIONES = [
  {
    icon: Play,
    get titulo() { return t("Start Trip"); },
    get texto() { return t("Open the load and read the Trip Recap: container, size, appointment and each of your stops with its address and hours. When you are ready to go, tap Start Trip."); },
  },
  {
    icon: Route,
    get titulo() { return t("Record arrival"); },
    get texto() { return t("At each stop, tap Record arrival when you get there. Then fill in what the screen asks for and tap the confirm button (for example, Confirm delivery and departure) to leave the stop. The next stop opens by itself."); },
  },
  {
    icon: ClipboardCheck,
    get titulo() { return t("Chassis and container"); },
    get texto() { return t("At pickup, enter the chassis number and the container number. If the container does not match the order, the app warns you before you continue."); },
  },
  {
    icon: Timer,
    get titulo() { return t("Delivery ETA"); },
    get texto() { return t("At Rail Pickup, enter the Delivery ETA (date, time and time zone) before you leave. If it changes, tap Update ETA. Dispatch sees every update."); },
  },
  {
    icon: Camera,
    get titulo() { return t("Add PDF or photo"); },
    get texto() { return t("Tap Add PDF or photo to take a picture or choose a file. For the signed DO / POD, align the paper inside the frame in good light. The system checks that it can be read and tells you what to fix before you save."); },
  },
  {
    icon: Undo2,
    get titulo() { return t("Back to previous step"); },
    get texto() { return t("Use Back to previous step to look at an earlier stop. Nothing you entered is lost. Tap Return to current step to continue where you were."); },
  },
  {
    icon: FileText,
    get titulo() { return t("Files (N)"); },
    get texto() { return t("Files (N) shows the PDFs and photos you uploaded for this load, with the step and the time of each one."); },
  },
  {
    icon: CircleCheckBig,
    get titulo() { return t("Finish Trip"); },
    get texto() { return t("On your last step, tap Finish Trip. The load then shows Completed. If a required document is missing, you will see Documents pending and you can add it later from the same load."); },
  },
  {
    icon: HandHelping,
    get titulo() { return t("I can’t finish this trip"); },
    get texto() { return t("Use it if you have to stop because of a mechanical problem, out of hours, a closed customer or another reason. Choose the reason; dispatch will reassign the steps you did not finish."); },
  },
  {
    icon: Fuel,
    get titulo() { return t("Fuel receipts"); },
    get texto() { return t("In the Fuel tab, tap Add fuel receipt, take a photo of the ticket and check the details the app filled in before saving."); },
  },
  {
    icon: PackageSearch,
    get titulo() { return t("Supplies and expenses"); },
    get texto() { return t("Use I need something to ask for supplies, or I bought something to report a purchase. Usual expenses (oil, wiper fluid) are recorded right away with the receipt photo; any other expense waits for approval."); },
  },
  {
    icon: AlertOctagon,
    get titulo() { return t("Report a problem"); },
    get texto() { return t("At the bottom of every screen you will find Report a problem. Use it for a tire, mechanical failure, accident or anything unexpected, with a photo if you can."); },
  },
  {
    icon: CalendarCheck,
    get titulo() { return t("Close my day"); },
    get texto() { return t("At the end of your shift, tap Close my day on the Loads screen. If something is missing, it tells you what before you can close. Documents still pending are listed so you can send them later."); },
  },
  {
    icon: KeyRound,
    get titulo() { return t("Change password"); },
    get texto() { return t("Tap More in the bottom bar and choose Change password whenever you want."); },
  },
];

export default function ChoferAyuda() {
  const navigate = useNavigate();
  const { iniciar: iniciarGuia } = useGuia(); // Prompt M
  return (
    <div className="bg-paper">
      <header className="bg-paper sticky top-0 z-10 border-b border-line">
        <div className="max-w-md mx-auto flex items-center gap-1 px-2 py-2">
          <button data-testid="ayuda-volver" onClick={() => navigate("/chofer")} className="h-12 w-12 flex items-center justify-center text-ink" aria-label={t("Back")}><ArrowLeft className="h-6 w-6" /></button>
          <h1 className="font-display font-bold text-[26px] leading-[1.05] text-ink">{t("Driver guide")}</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-3">
        <p className="text-[16px] text-ink-2 px-1">{t("A quick reminder of how to use the app. If you have questions, contact the office.")}</p>
        <Button data-testid="ayuda-ver-guia" className="w-full h-12" onClick={() => iniciarGuia("chofer-inicio", "/chofer")}><Compass className="h-5 w-5" />{t(" Show the app guide")}</Button>
        {SECCIONES.map((s, i) => (
          <div key={i} data-testid={`ayuda-seccion-${i}`} className="bg-white rounded-xl border border-line p-4 flex gap-3">
            <span className="h-9 w-9 rounded-full bg-navy text-white font-display font-bold text-[16px] flex items-center justify-center shrink-0">{i + 1}</span>
            <div>
              <p className="font-display font-semibold text-[18px] text-ink flex items-center gap-2"><s.icon className="h-5 w-5 text-ink-3" />{s.titulo}</p>
              <p className="text-[16px] text-ink-2 mt-1 leading-snug">{s.texto}</p>
            </div>
          </div>
        ))}
      </main>
    </div>
  );
}
